import React from "react";
import { FiLock, FiShield, FiDatabase, FiUsers, FiSettings, FiAlertCircle, FiClock, FiMail } from "react-icons/fi";

function Privacy() {
    // The specific effective date from your text
    const effectiveDate = "January 4, 2026";

    return (
        <div className="pt-20 pb-16 min-h-screen bg-gradient-to-b from-[#021428] via-[#011226] to-[#021428] relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 mb-6 shadow-lg shadow-blue-500/10">
                        <FiLock className="text-4xl text-blue-400" />
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-600 bg-clip-text text-transparent leading-tight">
                        Skylapse Privacy Policy
                    </h1>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full backdrop-blur-sm">
                        <FiClock className="text-blue-400" />
                        <p className="text-blue-200 text-sm font-medium">Effective date: {effectiveDate}</p>
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    {/* Section 1: Introduction */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <span className="text-2xl text-blue-400 font-bold">1</span>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Introduction
                            </h2>
                        </div>
                        <p className="text-blue-100 leading-relaxed text-base sm:text-lg">
                            This Privacy Policy explains how Skylapse handles information when you play our games or interact with our services. We aim to keep our practices clear and easy to understand.
                        </p>
                    </div>

                    {/* Section 2: Data We Collect */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiDatabase className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Data We Collect
                            </h2>
                        </div>
                        <p className="text-blue-100 mb-6 leading-relaxed text-base sm:text-lg">
                            To operate and improve our games, we may collect:
                        </p>
                        <div className="space-y-6">
                            {/* A. Info You Provide */}
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 backdrop-blur-sm">
                                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-blue-200 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                    A. Information you provide
                                </h3>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-3 text-blue-100">
                                        <span className="text-blue-400 mt-1.5">▸</span>
                                        <span>Your name or username, email address, and/or phone number (only if you choose to provide them).</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-blue-100">
                                        <span className="text-blue-400 mt-1.5">▸</span>
                                        <span>Your user ID and password (or other account credentials, if accounts are used).</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-blue-100">
                                        <span className="text-blue-400 mt-1.5">▸</span>
                                        <span>Any details you send to us through support messages or feedback.</span>
                                    </li>
                                </ul>
                            </div>

                            {/* B. Info Collected Automatically */}
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 backdrop-blur-sm">
                                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-blue-200 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                    B. Information collected automatically
                                </h3>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-3 text-blue-100">
                                        <span className="text-blue-400 mt-1.5">▸</span>
                                        <span>Device ID and IP address.</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-blue-100">
                                        <span className="text-blue-400 mt-1.5">▸</span>
                                        <span>Gameplay usage information such as playtime, progress, levels achieved, and scores.</span>
                                    </li>
                                </ul>
                            </div>

                            {/* C. Connected Platforms */}
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 backdrop-blur-sm">
                                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-blue-200 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                    C. Information from connected platforms
                                </h3>
                                <p className="text-blue-100 leading-relaxed">
                                    If you use third-party sign-in (e.g., Google Play Games or Apple Game Center), we may receive limited profile/account information according to your settings on those platforms.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: How We Use Data */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiSettings className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                How We Use Data
                            </h2>
                        </div>
                        <p className="text-blue-100 mb-4 leading-relaxed text-base sm:text-lg">
                            We use the collected information to:
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {[
                                "Deliver the core features of the game and related services.",
                                "Maintain accounts, save progress (where available), and improve quality and reliability.",
                                "Provide customer support and respond to your inquiries.",
                                "Monitor and troubleshoot technical issues and improve overall service operation.",
                                "Meet legal requirements and protect our users and systems."
                            ].map((item, idx) => (
                                <div key={idx} className={`bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 backdrop-blur-sm ${idx === 4 ? 'sm:col-span-2' : ''}`}>
                                    <div className="flex items-start gap-3">
                                        <span className="text-blue-400 mt-1">▸</span>
                                        <p className="text-blue-100 text-sm leading-relaxed">{item}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Section 4: Information Disclosure */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiUsers className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Information Disclosure
                            </h2>
                        </div>
                        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 mb-4 backdrop-blur-sm">
                            <div className="flex items-start gap-3">
                                <FiAlertCircle className="text-green-400 text-xl flex-shrink-0 mt-0.5" />
                                <p className="text-green-100 leading-relaxed">
                                    We do <strong className="text-green-200">not</strong> rent or sell your personal data.
                                </p>
                            </div>
                        </div>
                        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 backdrop-blur-sm">
                            <p className="text-blue-100 leading-relaxed">
                                We disclose information only when we must comply with applicable law, legal process, court orders, or official requests by authorized authorities.
                            </p>
                        </div>
                    </div>

                    {/* Section 5: Security & Retention */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiShield className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Security and Retention
                            </h2>
                        </div>
                        <div className="space-y-6">
                            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-6 backdrop-blur-sm">
                                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-cyan-300 flex items-center gap-2">
                                    <span className="text-2xl">🛡️</span>
                                    Security
                                </h3>
                                <p className="text-blue-100 leading-relaxed">
                                    We use reasonable safeguards to protect information, but no system can guarantee absolute security.
                                </p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6 backdrop-blur-sm">
                                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-blue-300 flex items-center gap-2">
                                    <span className="text-2xl">🗑️</span>
                                    Data Retention
                                </h3>
                                <p className="text-blue-100 mb-4 leading-relaxed">
                                    We retain data as long as necessary for the purposes described in this policy. If you request deletion (or delete your account where available), we will delete or anonymize data where feasible.
                                </p>
                                <div className="flex items-start gap-3 text-blue-100 bg-blue-900/30 p-3 rounded-lg border border-blue-500/20">
                                    <span className="text-blue-400 mt-0.5">ℹ️</span>
                                    <span className="text-sm">Some data may remain in secure backups for up to <strong>6 months</strong> for recovery and legal compliance before final removal.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 6: Your Choices */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiSettings className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Your Choices
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 backdrop-blur-sm">
                                <h4 className="font-semibold text-blue-200 mb-2">Access & Correction</h4>
                                <p className="text-blue-100 text-sm leading-relaxed">
                                    You may request access to, correction of, or deletion of your personal data by contacting us.
                                </p>
                            </div>
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 backdrop-blur-sm">
                                <h4 className="font-semibold text-blue-200 mb-2">Account Deletion</h4>
                                <p className="text-blue-100 text-sm leading-relaxed">
                                    You may request account deletion (if available) through in-game settings or by contacting us.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 7: Changes to Policy */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiClock className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Changes to This Policy
                            </h2>
                        </div>
                        <p className="text-blue-100 leading-relaxed text-base sm:text-lg">
                            We may revise this policy from time to time. The effective date at the top reflects the latest version.
                        </p>
                    </div>

                    {/* Section 8: Contact */}
                    <div className="bg-gradient-to-br from-[#011426]/80 to-[#001424]/80 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:shadow-blue-500/10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center border border-blue-500/30">
                                <FiMail className="text-xl text-blue-400" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                Contact
                            </h2>
                        </div>
                        <p className="text-blue-100 leading-relaxed text-base sm:text-lg mb-4">
                            For privacy questions or requests, contact Skylapse:
                        </p>
                        <div className="inline-block">
                             <a href="mailto:support@skylapse.com" className="flex items-center gap-3 px-6 py-4 bg-blue-500/10 border border-blue-500/30 rounded-xl hover:bg-blue-500/20 transition-colors cursor-pointer group">
                                <FiMail className="text-blue-400 group-hover:text-blue-300 transition-colors" />
                                <span className="text-blue-200 font-medium group-hover:text-blue-100 transition-colors">contact@skylapsegames.com</span>
                             </a>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Privacy;